import React, { useState, useRef, useEffect } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * HTMLArtifactPreview Component
 *
 * Renders HTML artifacts with:
 * - Toggle between code view and live preview
 * - Sandboxed iframe for secure HTML execution
 * - Syntax highlighted code view
 * - Copy button
 * - Refresh preview button
 */
function HTMLArtifactPreview({ content, title, isEditing = false, onContentChange }) {
  const [viewMode, setViewMode] = useState('preview'); // 'preview' or 'code'
  const [copied, setCopied] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const iframeRef = useRef(null);
  const textareaRef = useRef(null);

  // Switch to split view when editing starts
  useEffect(() => {
    if (isEditing) {
      setViewMode('edit');
    } else if (viewMode === 'edit') {
      setViewMode('preview');
    }
  }, [isEditing]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing && viewMode === 'edit' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing, viewMode]);

  // Handle content changes in edit mode
  const handleChange = (e) => {
    if (onContentChange) {
      onContentChange(e.target.value);
    }
  };

  // Calculate line numbers for editor
  const lineCount = (content || '').split('\n').length;

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Refresh the iframe preview
  const handleRefresh = () => {
    setIframeKey(prev => prev + 1);
  };

  // Generate full HTML document if content is a fragment
  const getFullHtmlDocument = (htmlContent) => {
    // Check if content already has html/head/body structure
    const hasHtmlTag = /<html[\s>]/i.test(htmlContent);
    const hasBodyTag = /<body[\s>]/i.test(htmlContent);

    if (hasHtmlTag && hasBodyTag) {
      return htmlContent;
    }

    // Wrap fragment in a basic HTML document
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    * {
      box-sizing: border-box;
    }
    body {
      font-family: system-ui, sans-serif;
      margin: 0;
      padding: 16px;
      line-height: 1.5;
    }
  </style>
</head>
<body>
${htmlContent}
</body>
</html>`;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar with view toggle */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        {/* View mode toggle */}
        <div className="flex items-center bg-gray-700 rounded-lg p-0.5">
          {isEditing && (
            <button
              onClick={() => setViewMode('edit')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'edit'
                  ? 'bg-gray-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </span>
            </button>
          )}
          <button
            onClick={() => setViewMode('preview')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              viewMode === 'preview'
                ? 'bg-gray-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Preview
            </span>
          </button>
          <button
            onClick={() => setViewMode('code')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              viewMode === 'code'
                ? 'bg-gray-600 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              Code
            </span>
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1">
          {viewMode === 'preview' && (
            <button
              onClick={handleRefresh}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-400 hover:text-white
                hover:bg-gray-700 rounded transition-colors"
              title="Refresh preview"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          )}
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-400 hover:text-white
              hover:bg-gray-700 rounded transition-colors"
            title={copied ? 'Copied!' : 'Copy HTML'}
          >
            {copied ? (
              <>
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-500">Copied!</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'edit' ? (
          // Split view - Editor on left, Preview on right
          <div className="flex h-full">
            {/* Code editor panel */}
            <div className="w-1/2 h-full overflow-auto bg-gray-900 border-r border-gray-700">
              <div className="flex h-full">
                {/* Line numbers column */}
                <div className="shrink-0 py-4 pl-4 pr-0 text-right text-gray-500 font-mono text-sm select-none"
                  style={{ minWidth: '3em', lineHeight: '1.6' }}
                >
                  {Array.from({ length: lineCount }, (_, i) => (
                    <div key={i + 1}>{i + 1}</div>
                  ))}
                </div>
                {/* Editable textarea */}
                <textarea
                  ref={textareaRef}
                  value={content || ''}
                  onChange={handleChange}
                  className="flex-1 bg-transparent text-gray-100 font-mono text-sm p-4 pl-4 resize-none focus:outline-hidden"
                  style={{
                    lineHeight: '1.6',
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    tabSize: 2,
                  }}
                  spellCheck={false}
                  placeholder="<!-- Enter your HTML here... -->"
                />
              </div>
            </div>
            {/* Live preview panel */}
            <div className="w-1/2 h-full p-2 bg-gray-100 dark:bg-gray-900">
              <div className="h-full rounded-lg overflow-hidden bg-white shadow-xs border border-gray-200 dark:border-gray-700">
                <iframe
                  key={content} // Re-render on content change for live preview
                  srcDoc={getFullHtmlDocument(content)}
                  className="w-full h-full"
                  title={title || 'HTML Preview'}
                  sandbox="allow-scripts allow-same-origin"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>
          </div>
        ) : viewMode === 'preview' ? (
          // Live preview with sandboxed iframe
          <div className="h-full p-4 bg-gray-100 dark:bg-gray-900">
            <div className="h-full rounded-lg overflow-hidden bg-white shadow-xs border border-gray-200 dark:border-gray-700">
              <iframe
                ref={iframeRef}
                key={iframeKey}
                srcDoc={getFullHtmlDocument(content)}
                className="w-full h-full"
                title={title || 'HTML Preview'}
                // Security sandbox - allows scripts but restricts other behaviors
                sandbox="allow-scripts allow-same-origin"
                // Additional security headers
                referrerPolicy="no-referrer"
              />
            </div>
          </div>
        ) : (
          // Code view with syntax highlighting
          <div className="h-full overflow-auto bg-gray-900">
            <SyntaxHighlighter
              language="html"
              style={oneDark}
              showLineNumbers={true}
              wrapLines={true}
              lineNumberStyle={{
                minWidth: '3em',
                paddingRight: '1em',
                textAlign: 'right',
                color: '#6b7280',
                userSelect: 'none',
              }}
              customStyle={{
                margin: 0,
                padding: '1rem',
                fontSize: '0.875rem',
                lineHeight: '1.6',
                background: 'transparent',
                minHeight: '100%',
              }}
              codeTagProps={{
                style: {
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                }
              }}
            >
              {content || '<!-- No HTML content -->'}
            </SyntaxHighlighter>
          </div>
        )}
      </div>

      {/* Preview info bar */}
      {viewMode === 'preview' && (
        <div className="px-4 py-2 bg-gray-800 border-t border-gray-700 text-xs text-gray-400 flex items-center gap-2">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Running in sandboxed iframe with limited permissions</span>
        </div>
      )}
    </div>
  );
}

export default HTMLArtifactPreview;
